import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { createCustomerProfile } from "data/server_apis/accountService";
import {
  getCustomers,
  deleteCustomer,
  activateCustomer,
  updateCustomer,
} from "data/server_apis/customerService";

const customerAdapter = createEntityAdapter({
  selectId: (customer) => customer.id,
});

export const getCustomerThunk = createAsyncThunk(
  "customers/get",
  async ({ page, filters }, { rejectWithValue }) => {
    try {
      return (await getCustomers(page, filters)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const createCustomerProfileThunk = createAsyncThunk(
  "accounts/createCustomerProfile",
  async (data, { rejectWithValue }) => {
    try {
      const response = (await createCustomerProfile(data)).data;
      console.log(response);
      return response;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const updateCustomerThunk = createAsyncThunk(
  "customers/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return (await updateCustomer(id, data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const deleteCustomerThunk = createAsyncThunk(
  "customers/delete",
  async (item, { rejectWithValue }) => {
    try {
      return (await deleteCustomer(item.id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const activateCustomerThunk = createAsyncThunk(
  "customers/activate",
  async (item, { rejectWithValue }) => {
    try {
      return (await activateCustomer(item.id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

const customerSlice = createSlice({
  name: "customer",
  initialState: customerAdapter.getInitialState({
    status: {},
    totalPages: 1,
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerThunk.pending, (state, action) => {
        state.status = { loading: "get" };
      })
      .addCase(getCustomerThunk.rejected, (state, action) => {
        state.status = { error: "get", errorMessage: action.payload?.message };
      })
      .addCase(getCustomerThunk.fulfilled, (state, action) => {
        state.status = { success: "get" };
        customerAdapter.setAll(state, action.payload.results);
        state.totalPages = action.payload.total_pages;
      });

      builder
      .addCase(createCustomerProfileThunk.pending, (state, action) => {
        state.status = { loading: "create" };
      })
      .addCase(createCustomerProfileThunk.rejected, (state, action) => {
        state.status = { error: "create", errorMessage: action.payload?.message };
      })
      .addCase(createCustomerProfileThunk.fulfilled, (state, action) => {
        state.status = { success: "create" };
        customerAdapter.addOne(state, action.payload);
      });

      builder
      .addCase(updateCustomerThunk.pending, (state, action) => {
        state.status = { loading: "update", id: action.meta.arg.id };
      })
      .addCase(updateCustomerThunk.rejected, (state, action) => {
        state.status = {
          error: "update",
          errorMessage: action.payload?.message,
          id: action.meta.arg.id,
        };
      })
      .addCase(updateCustomerThunk.fulfilled, (state, action) => {
        state.status = { success: "update" };
        customerAdapter.updateOne(state, {
          id: action.meta.arg.id,
          changes: action.payload,
        });
      });

    builder
      .addCase(deleteCustomerThunk.pending, (state, action) => {
        state.status = { loading: "delete", id: action.meta.arg.id };
      })
      .addCase(deleteCustomerThunk.rejected, (state, action) => {
        state.status = {
          error: "delete",
          errorMessage: action.payload?.message,
          id: action.meta.arg.id,
        };
      })
      .addCase(deleteCustomerThunk.fulfilled, (state, action) => {
        state.status = { success: "delete", id: action.meta.arg.id };
        customerAdapter.removeOne(state, action.meta.arg.id);
      });

    builder
      .addCase(activateCustomerThunk.pending, (state, action) => {
        state.status = { loading: "activate", id: action.meta.arg.id };
      })
      .addCase(activateCustomerThunk.rejected, (state, action) => {
        state.status = {
          error: "activate",
          errorMessage: action.payload?.message,
          id: action.meta.arg.id,
        };
      })
      .addCase(activateCustomerThunk.fulfilled, (state, action) => {
        state.status = { success: "activate", id: action.meta.arg.id };
        customerAdapter.updateOne(state, {
          id: action.meta.arg.id,
          changes: { ...action.meta.arg, active: true },
        });
      });
  },
});

export const customerSelector = customerAdapter.getSelectors(
  (state) => state.customers
);

export default customerSlice.reducer;
